import { useMemo, useState } from 'react';
import {
  useGetAssignmentsSummaryQuery,
  useGetAssignmentsPerformanceQuery,
  downloadDashboardExport,
  DASHBOARD_EXPORT_PATHS,
  type AssignmentAssessmentType,
  type AssignmentsPerformanceParams,
  type AssignmentsSummaryParams,
} from '@/entities/Dashboard';
import { CustomInput } from '@/shared/ui/Fields';
import { Svg } from '@/shared/ui/Svg';
import SearchIcon from '@/shared/assets/icons/search.svg';
import NoteIcon from '@/shared/assets/icons/dashboard/Note.svg';
import { searchInputClassName } from '@/shared/config/searchInputClassName';
import { useDebounce } from '@/shared/lib/hooks/useDebounce';
import { useToast } from '@/shared/lib/hooks/useToast';
import { buildMetric } from '../../../lib/metrics';
import { formatPercentOrDash } from '../../../lib/rows';
import {
  DASHBOARD_DATE_PERIOD_OPTIONS,
  toDashboardDateParam,
  type DashboardDatePeriod,
} from '../../../lib/datePeriod';
import type { MetricItem } from '../../../config/mockDashboardData';
import { AnalyticsMetricsGrid } from '../../components/AnalyticsMetricCard';
import { AnalyticsSectionHeader } from '../../components/AnalyticsSectionHeader';
import { DashboardFilters, type DashboardFilterValue } from '../../components/DashboardFilters';
import { AnalyticsPagination, AnalyticsSubTabs } from '../../components/AnalyticsTable';
import styles from '../../dashboard/Dashboard.module.scss';

const ASSIGNMENT_SUB_TABS = [
  { key: 'formative', label: 'Formative' },
  { key: 'summative', label: 'Summative' },
];

const PAGE_SIZE = 8;

interface AssignmentsTestsTabProps {
  onExport?: () => void;
}

export const AssignmentsTestsTab = ({ onExport: _onExport }: AssignmentsTestsTabProps) => {
  const { toast } = useToast();
  const [datePeriod, setDatePeriod] = useState<DashboardDatePeriod>('this-month');
  const [subTab, setSubTab] = useState<AssignmentAssessmentType>('formative');
  const [filters, setFilters] = useState<DashboardFilterValue>({});
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [isExporting, setIsExporting] = useState(false);
  const debouncedSearch = useDebounce(search.trim().toLowerCase(), 300);
  const date = toDashboardDateParam(datePeriod);
  const canFetch = Boolean(filters.courseId && filters.intakeId);

  const summaryParams = useMemo<AssignmentsSummaryParams | null>(() => {
    if (!filters.courseId || !filters.intakeId) return null;
    return {
      courseId: filters.courseId,
      intakeId: filters.intakeId,
      date,
      groupId: filters.groupId,
    };
  }, [filters.courseId, filters.intakeId, filters.groupId, date]);

  const performanceParams = useMemo<AssignmentsPerformanceParams | null>(() => {
    if (!filters.courseId || !filters.intakeId) return null;
    return {
      courseId: filters.courseId,
      intakeId: filters.intakeId,
      date,
      groupId: filters.groupId,
      assessmentType: subTab,
    };
  }, [filters.courseId, filters.intakeId, filters.groupId, date, subTab]);

  const { data: summary } = useGetAssignmentsSummaryQuery(summaryParams as AssignmentsSummaryParams, {
    skip: !summaryParams,
  });

  const { data: performance } = useGetAssignmentsPerformanceQuery(
    performanceParams as AssignmentsPerformanceParams,
    { skip: !performanceParams },
  );

  const metrics = useMemo<MetricItem[]>(() => {
    const s = summary?.data;
    return [
      buildMetric('1', 'Awaiting Review', s?.awaitingReview, 'count'),
      buildMetric('2', 'Submitted on Time', s?.submittedOnTime, 'percent'),
      buildMetric('3', 'Resubmission Rate', s?.resubmissionRate, 'percent'),
      buildMetric('4', 'Failed (1st Submission)', s?.failedFirstResubmission, 'count'),
      buildMetric('5', 'Failed (2nd Submission)', s?.failedSecondResubmission, 'count'),
      buildMetric('6', 'Withdrawal', s?.withdrawal, 'count'),
    ];
  }, [summary]);

  const filteredModules = useMemo(() => {
    const modules = performance?.data?.modules ?? [];
    if (!debouncedSearch) return modules;
    return modules.filter((row) => row.title.toLowerCase().includes(debouncedSearch));
  }, [performance, debouncedSearch]);

  const pageCount = Math.max(1, Math.ceil(filteredModules.length / PAGE_SIZE));
  const safePage = Math.min(page, pageCount);
  const pagedModules = filteredModules.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);

  const handleFilters = (value: DashboardFilterValue) => {
    setFilters(value);
    setPage(1);
  };

  const handleSubTab = (key: string) => {
    setSubTab(key as AssignmentAssessmentType);
    setPage(1);
  };

  const handleSearch = (value: string) => {
    setSearch(value);
    setPage(1);
  };

  const handleExport = async () => {
    if (!filters.courseId || !filters.intakeId) {
      toast({
        title: 'Error',
        description: 'Select a course and intake to export',
        color: 'danger',
      });
      return;
    }

    setIsExporting(true);
    try {
      await downloadDashboardExport(
        DASHBOARD_EXPORT_PATHS.assignments,
        {
          format: 'excel',
          courseId: filters.courseId,
          intakeId: filters.intakeId,
          groupId: filters.groupId,
          date,
          sections: 'summary,performance',
        },
        'assignments-analytics.xlsx',
      );
      toast({ title: 'Success', description: 'Export completed', color: 'success' });
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error?.message || 'Failed to export analytics',
        color: 'danger',
      });
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <div className={styles.root}>
      <section className={styles.section}>
        <AnalyticsSectionHeader
          title="Assignments & Tests"
          icon={<Svg Icon={NoteIcon} width={20} height={20} className="text-[#1E7E34]" />}
          iconBg="#E8F8EE"
          onExport={isExporting ? undefined : handleExport}
          periodOptions={DASHBOARD_DATE_PERIOD_OPTIONS}
          periodValue={datePeriod}
          onPeriodChange={(key) => setDatePeriod(key as DashboardDatePeriod)}
        />
        <DashboardFilters value={filters} onChange={handleFilters} showTerm twoRow />
        {!canFetch ? (
          <p className="text-sm text-grey-secondary">Select a course and intake to see summary.</p>
        ) : null}
        <AnalyticsMetricsGrid metrics={metrics} columns={6} />
      </section>

      <section className={styles.section}>
        <AnalyticsSectionHeader
          title="Assignment Performance"
          icon={<Svg Icon={NoteIcon} width={20} height={20} className="text-[#0068D6]" />}
          iconBg="#E8F0FF"
          showToolbar={false}
        />
        <AnalyticsSubTabs tabs={ASSIGNMENT_SUB_TABS} value={subTab} onChange={handleSubTab} />
        <CustomInput aria-label="Search"
          className={searchInputClassName}
          startContent={<Svg Icon={SearchIcon} />}
          placeholder="Search"
          value={search}
          onValueChange={handleSearch}
        />
        <div className={styles.tableWrap}>
          <table className={styles.table}>
            <caption className="sr-only">Assignment performance by module</caption>
            <thead>
              <tr>
                <th scope="col">Module name</th>
                <th scope="col">Number of students</th>
                {subTab === 'summative' ? <th scope="col">Avg. Grade</th> : null}
                <th scope="col">Submitted on Time</th>
                <th scope="col">Awaiting Review</th>
                <th scope="col">Resubmission</th>
                <th scope="col">Unsubmitted</th>
              </tr>
            </thead>
            <tbody>
              {pagedModules.map((row) => (
                <tr key={row.id}>
                  <td>{row.title}</td>
                  <td>{row.studentsCount}</td>
                  {subTab === 'summative' ? (
                    <td>{formatPercentOrDash(row.passRate)}</td>
                  ) : null}
                  <td>{row.submittedOnTimeCount}</td>
                  <td>{row.awaitingReviewCount}</td>
                  <td>{row.resubmissionCount}</td>
                  <td>{row.unsubmittedCount}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <AnalyticsPagination page={safePage} pageCount={pageCount} onPageChange={setPage} />
      </section>
    </div>
  );
};
